import { accessModeTranslations, ICameraGroup } from 'app/models/camera.model';
import { Component, OnInit, Input, EventEmitter, Output, OnChanges, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { ApiService } from "app/services/api.service";
import { CameraAlarmTypeService, IActivationTranslation, TECHNICAL_ALARM_GROUPS, EXCLUDED_ALARM_TYPES } from "../camera-alarm-type.service";
import { BehaviorSubject, EMPTY, Observable } from "rxjs";
import { TranslateModule } from "@ngx-translate/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Device } from "app/models/device.model";
import { SelectCamerasComponent } from "../select-cameras/select-cameras.component";
import { IGroupedOption, IOption, MultiselectDropdownComponent } from "app/components/multiselect-dropdown/multiselect-dropdown.component";
import { ICamerasByGroup } from "../camera-configuration.component";
import { CameraScheduleService } from "app/services/camera-schedule.service";
import { IRemovedScheduledVisit } from 'app/models/activity-log.model';
import { catchError, concatMap, map, switchMap, tap } from "rxjs/operators";
import { ThrottleButtonClickDirective } from "app/directives/throttle-button-click.directive";
import { CameraVisit } from "app/models/camerasupervision.model";
import { VideoServerService } from "app/services/videoserver.service";
import {
  DeviceNamePipe,
  ReceiverTeamNamePipe,
} from "../camera-configuration-pipes";
import {
  NewCameraGroupForm,
  CameraGroupEditForm,
  AlarmTypeKey,
  IVisitConfiguration,
  VisitOnAlertConfig,
  VisitTimeOption,
  AccessMode,
  VideoServerCamera,
  DeviceType
} from "app/models/camera.model";

interface ISelectedLocation { id: string, name: string };
interface ISelectedOrigon { id: string, name: string, locationId: string, gsmNumber: string };
interface IAlarmTypeOption {
  key: AlarmTypeKey;
  keyStr: string;
  alarmName: string;
  label: string;
  checked: boolean;
}



@Component({
    selector: "camera-group-form",
    templateUrl: "./camera-group-form.component.html",
    styleUrls: ["../camera-configuration.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        MultiselectDropdownComponent,
        MatSlideToggleModule,
        ThrottleButtonClickDirective,
        MatOptionModule,
        MatSelectModule,
        MatFormFieldModule,
        DeviceNamePipe,
        ReceiverTeamNamePipe,
        SelectCamerasComponent,
        MatTooltipModule,
    ]
})
export class CameraGroupFormComponent implements OnInit, OnChanges {
  constructor(
    private api: ApiService,
    private scheduleService: CameraScheduleService,
    private modalService: NgbModal,
    private videoServerService: VideoServerService,
    private alarmTypeService: CameraAlarmTypeService
  ) { }

  @Input() mode: 'add' | 'edit' = 'add';
  @Input() showForm: BehaviorSubject<boolean>;
  @Input() isNightowlServiceCustomerIdSet: boolean = false;

  // For edit mode
  @Input() existingGroup?: ICameraGroup;
  @Input() existingVisitConfig?: IVisitConfiguration;

  // Select option data variables
  @Input() alertDeviceSelect: IOption[] = [];
  @Input() receiverTeamSelect: IGroupedOption[] = [];
  @Input() locationSelect: { id: string; name: string }[] = [];
  @Input() origonSelect: { id: string; name: string, locationId: string, gsmNumber: string }[] = [];
  @Input() cameraList: Device[];
  @Input() deviceTypeByDeviceId: Record<string, string> = {};
  @Input() assignedCameras: ICamerasByGroup[];

  @Output() cameraGroupChanged = new EventEmitter<string>();

  cameraGroupForm: NewCameraGroupForm | CameraGroupEditForm;
  visitConfigForm: VisitOnAlertConfig;

  user: any;

  showCameraExistsErrorMsg: boolean = false;
  showSubmitErrorMsg: boolean = false;
  submitErrorMessage: string = "";
  selectedAlertDevices: string[] = [];
  camerasOfLocation: IGroupedOption[];
  origonsOfLocation: IGroupedOption[];
  selectedLocation: ISelectedLocation;
  selectedOrigon: ISelectedOrigon;
  selectedOrigonId: string;
  selectedDeviceType: string = "Camera";
  cameraSelectDisabled = new BehaviorSubject<boolean>(true);
  hasNightowlDevices: boolean = false;
  visitTimeMax: number = 120;
  graceTimeDisabled: boolean = false;
  origonGsmNumberMissing: boolean = false;
  availableAlarmTypes: IAlarmTypeOption[] = [];
  alarmTypeSelect: IGroupedOption[] = [];
  selectedAlarmTypes: string[] = [];

  // Cache for Nightowl visits to avoid redundant API calls
  private cachedNightowlVisits: CameraVisit[] | null = null;

  // Track original pause state for Nightowl resume on save
  private originalPauseState: boolean = false;

  // Pause schedule properties
  private pauseExpired: boolean = false;
  pauseStartDate: string = "";
  pauseStartTime: string = "";
  resumeDate: string = "";
  resumeTime: string = "";
  pauseScheduleError: string = "";

  get todayDateString(): string {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }

  // Paused visits tracking
  pausedVisitsCount: number = 0;
  showPausedVisitsInfo: boolean = false;
  pausedVisitsMessage: string = "";
  loadingVisitsCount: boolean = false;

  // Edit mode specific
  confirmDialogOpened: boolean = false;
  waitingResponse: boolean = false;
  dialogErrorMsg: string = "";

  visitAffectingChanges = {
    removedCameras: [],
    removedReceiverTeams: [],
    accessModeChange: "",
    clear(): void {
      this.removedCameras = [];
      this.removedReceiverTeams = [];
      this.accessModeChange = "";
    }
  };

  ngOnInit(): void {
    this.user = JSON.parse(localStorage.getItem("user"));

    if (this.mode === 'edit' && this.existingGroup) {
      this.initEditMode();
    } else {
      this.initAddMode();
      // For add mode, init camera/origon select if data is ready
      if (this.cameraList && this.alertDeviceSelect && this.locationSelect) {
        this.initCameraSelect();
        this.initOrigonSelect();
      }
    }

    // show deviceType select if there are Nightowl devices in the list
    this.hasNightowlDevices = this.cameraList?.some(
      (camera) => camera.deviceType == "34-4"
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Ensure user is loaded from localStorage if not already
    if (!this.user) {
      this.user = JSON.parse(localStorage.getItem("user"));
    }

    if (changes.existingGroup && !changes.existingGroup.firstChange) {
      if (this.mode === 'edit' && this.existingGroup) {
        this.initEditMode();
      }
    }

    if (this.mode === 'add' && !this.cameraGroupForm) {
      this.initAddMode();
    }
    
    // Only init camera/origon select if form is already initialized
    if (this.cameraList && this.alertDeviceSelect && this.locationSelect && this.cameraGroupForm) {
      this.initCameraSelect();
      this.initOrigonSelect();

      this.hasNightowlDevices = this.cameraList?.some(
        (camera) => camera.deviceType == "34-4"
      );
    }
  }

  private initAddMode(): void {
    this.cameraGroupForm = new NewCameraGroupForm(this.user);
    this.visitConfigForm = new VisitOnAlertConfig();
    this.selectedDeviceType = "Camera";
    this.submitErrorMessage = "ADD_CAMERA_GROUP_FAILED";
    this.cameraSelectDisabled.next(true);
    this.refreshAlarmTypeOptions();
  }

  private initEditMode(): void {
    this.cameraGroupForm = new CameraGroupEditForm(this.existingGroup);
    this.selectedDeviceType = this.existingGroup.deviceType;
    this.submitErrorMessage = "UPDATE_CAMERA_GROUP_FAILED_ERROR";

    // Store original pause state for comparison on save
    this.originalPauseState = this.existingGroup.isPaused || false;

    if (this.selectedDeviceType === 'Nightowl') {
      this.graceTimeDisabled = true;
      this.visitTimeMax = 30;
    } else {
      this.graceTimeDisabled = false;
      this.visitTimeMax = 120;
    }

    this.selectedLocation = this.locationSelect?.find(
      location => location.id === this.cameraGroupForm.locationId
    );

    this.selectedOrigon = this.origonSelect?.find(
      origon => origon.id === (this.cameraGroupForm as CameraGroupEditForm).origonId
    );
    this.selectedOrigonId = this.selectedOrigon?.id;

    if (this.existingVisitConfig) {
      this.visitConfigForm = structuredClone(this.existingVisitConfig);
      this.setSelectedAlertDevices(this.visitConfigForm?.alertSources);
      this.refreshAlarmTypeOptions();
      this.syncAlarmTypesToConfig();
    }

    // Always reset fields first so stale values from a previous group don't leak through.
    this.pauseStartDate = "";
    this.pauseStartTime = "";
    this.resumeDate = "";
    this.resumeTime = "";
    this.pauseScheduleError = "";

    this.pauseExpired = !!(this.existingGroup.pauseEnd && new Date(this.existingGroup.pauseEnd) < new Date());
    if (this.pauseExpired) {
      (this.cameraGroupForm as CameraGroupEditForm).isPaused = false;
    }
    // Load pause schedule data from model - only if valid dates exist and pause hasn't expired.
    // Nightowl stores local datetime strings; Camera stores UTC ISO strings.
    const loadDateTime = (value: string): { date: string, time: string } => {
      if (this.selectedDeviceType === 'Nightowl') {
        const parts = value.split('T');
        return { date: parts[0], time: parts[1]?.substring(0, 5) || '' };
      } else {
        const d = new Date(value);
        return {
          date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
          time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
        };
      }
    };
    if (!this.pauseExpired) {
      if (this.existingGroup.pauseStart) {
        const { date, time } = loadDateTime(this.existingGroup.pauseStart);
        this.pauseStartDate = date;
        this.pauseStartTime = time;
      }
      if (this.existingGroup.pauseEnd) {
        const { date, time } = loadDateTime(this.existingGroup.pauseEnd);
        this.resumeDate = date;
        this.resumeTime = time;
      }
    }

    this.cameraSelectDisabled.next(false);

    // Initialize origonsOfLocation first
    this.initOrigonSelect();

    // Enable origons for the current location
    if (this.cameraGroupForm.locationId && this.origonsOfLocation) {
      this.origonsOfLocation = [...this.selectOrigonsOfThisLocation(this.cameraGroupForm.locationId, this.origonsOfLocation)];
    }

    // Initialize camera select after form is ready
    this.initCameraSelect();
  }

  submit(): void {
    if (this.mode === 'add') {
      this.submitAdd();
    } else {
      this.validateAndUpdate();
    }
  }

  private submitAdd(): void {
    this.normalizeAlarmTypesForSave();

    if (this.cameraGroupForm.deviceType === DeviceType.Nightowl) {
      if (this.visitConfigForm.options.visitTimeMinutes > 30) {
        this.visitConfigForm.options.visitTimeMinutes = 30;
      }
    }

    let visitConfiguration = null;
    if (this.cameraGroupForm.accessMode === AccessMode.VisitOnAlert ||
      (this.cameraGroupForm.deviceType === DeviceType.Nightowl && this.visitConfigForm.alertSources.length > 0)) {
      visitConfiguration = this.visitConfigForm;
      if (this.cameraGroupForm.deviceType === DeviceType.Nightowl) {
        visitConfiguration.type = "alertsForNightowlCameraGroup";
      }
    }

    this.showSubmitErrorMsg = false;

    this.api.addCameraGroup({
      cameraGroup: this.cameraGroupForm as NewCameraGroupForm,
      visitConfiguration: visitConfiguration,
    }).subscribe(
      () => {
        this.clearNightowlVisitsCache();
        this.cameraGroupChanged.emit("CAMERA_GROUP_ADDED_SUCCESS");
        this.showForm.next(false);
      },
      (error) => {
        this.showSubmitErrorMsg = true;
        this.submitErrorMessage = "ADD_CAMERA_GROUP_FAILED";

        const errorMsg = typeof error === 'string' ? error : error?.error || '';

        if (typeof errorMsg === 'string' && errorMsg.startsWith("TranslationCode:")) {
          const key = errorMsg.substring("TranslationCode:".length).trim().split(/[.: ]/)[0];
          if (key && key.length > 0) {
            this.submitErrorMessage = key;
          }
        }

        console.error("Error adding camera group:", error);
      }
    );
  }

  validateAndUpdate(dialogEl?: any): void {
    this.showSubmitErrorMsg = false;
    this.normalizeAlarmTypesForSave();

    if (this.cameraGroupForm.deviceType === DeviceType.Nightowl && this.visitConfigForm?.options) {
      this.visitConfigForm.options.visitTimeMinutes = 30;
    }

    // Validate and convert pause schedule
    const editForm = this.cameraGroupForm as CameraGroupEditForm;
    if (!this.validatePauseSchedule()) {
      this.showSubmitErrorMsg = true;
      this.submitErrorMessage = this.pauseScheduleError;
      return;
    }

    // Nightowl: save as local datetime string; Camera: save as UTC ISO string
    const toSavedDateTime = (dateStr: string, timeStr: string): string => {
      if (this.selectedDeviceType === 'Nightowl') {
        return `${dateStr}T${timeStr}`;
      }
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hours, minutes] = timeStr.split(':').map(Number);
      // Construct from local time so .toISOString() correctly offsets to UTC
      const d = new Date(year, month - 1, day, hours, minutes);
      return d.toISOString();
    };
    if (this.pauseStartDate && this.pauseStartTime) {
      editForm.pauseStart = toSavedDateTime(this.pauseStartDate, this.pauseStartTime);
    } else {
      editForm.pauseStart = undefined;
    }
    if (this.resumeDate && this.resumeTime) {
      editForm.pauseEnd = toSavedDateTime(this.resumeDate, this.resumeTime);
    } else {
      editForm.pauseEnd = undefined;
    }

    if (this.hasCameraGroupChangesAffectingVisits()) {
      this.openDialog(dialogEl, { centered: true, size: 'lg' });
    } else {
      this.waitingResponse = true;

      this.updateCameraGroup$([]).subscribe(
        () => {
          this.waitingResponse = false;
          this.clearNightowlVisitsCache();
          
          // If Nightowl group and pause was ON but is now OFF, resume devices
          this.checkAndResumeNightowlOnSave();
          
          this.cameraGroupChanged.emit("CAMERA_GROUP_UPDATED");
          this.showForm.next(false);
        },
        (error) => {
          console.log(error);
          this.waitingResponse = false;
          this.showSubmitErrorMsg = true;
          this.submitErrorMessage = "UPDATE_CAMERA_GROUP_FAILED_ERROR";

          const errorMsg = typeof error === 'string' ? error : error?.error || '';

          if (typeof errorMsg === 'string' && errorMsg.startsWith("TranslationCode:")) {
            const key = errorMsg.substring("TranslationCode:".length).trim().split(/[.: ]/)[0];
            if (key && key.length > 0) {
              this.submitErrorMessage = key;
            }
          }
        }
      );
    }
  }

  hasCameraGroupChangesAffectingVisits(): boolean {
    if (this.mode === 'add') return false;

    let result = false;
    const originalGroup = this.existingGroup;
    const modifiedGroup = this.cameraGroupForm;

    this.visitAffectingChanges.clear();

    // Dialog should not be opened if the access mode is 'always' or 'visit on alert'
    if (originalGroup.accessMode) {
      return false;
    }

    if (this.anyCamerasRemoved(originalGroup, modifiedGroup)) {
      result = true;
    }

    if (this.anyReceiverTeamsRemoved(originalGroup, modifiedGroup)) {
      result = true;
    }

    if (!originalGroup.accessMode && modifiedGroup.accessMode) {
      this.visitAffectingChanges.accessModeChange = accessModeTranslations.get(modifiedGroup.accessMode);
      result = true;
    }

    return result;
  }

  anyCamerasRemoved(originalGroup: ICameraGroup, modifiedGroup: CameraGroupEditForm | ICameraGroup | NewCameraGroupForm): boolean {
    const removedCameras: string[] = [];

    const doesCameraExistInGroup = (cameraId: string, group: ICameraGroup | CameraGroupEditForm | NewCameraGroupForm): boolean => {
      return group.cameras.some((camera) => camera.discoveryName === cameraId);
    }

    for (const originalCamera of (originalGroup?.cameras ?? [])) {
      if (!doesCameraExistInGroup(originalCamera.discoveryName, modifiedGroup)) {
        removedCameras.push(`${originalCamera.displayName} (${originalCamera.discoveryName})`);
      }
    }

    if (removedCameras.length > 0) {
      this.visitAffectingChanges.removedCameras = removedCameras;
      return true;
    }

    return false;
  }

  anyReceiverTeamsRemoved(originalGroup: ICameraGroup, modifiedGroup: CameraGroupEditForm | ICameraGroup | NewCameraGroupForm): boolean {
    const removedUserGroups: string[] = [];
    const receiverTeamMap = new Map<string, IOption>(
      this.receiverTeamSelect.flatMap(group => group.options.map(option => [option.value, option]))
    );

    const doesReceiverTeamExistInGroup = (receiverTeamId: string, group: ICameraGroup | CameraGroupEditForm | NewCameraGroupForm): boolean => {
      return group?.userGroups?.includes(receiverTeamId);
    }

    for (const userGroupId of (originalGroup?.userGroups ?? [])) {
      if (!doesReceiverTeamExistInGroup(userGroupId, modifiedGroup)) {
        const userGroup = receiverTeamMap.get(userGroupId);
        removedUserGroups.push(`${userGroup?.name} (${userGroup?.value})`);
      }
    }

    if (removedUserGroups.length > 0) {
      this.visitAffectingChanges.removedReceiverTeams = removedUserGroups;
      return true;
    }

    return false;
  }

  clearVisitsAndUpdateGroup(): void {
    this.clearVisitsAndUpdateGroup$().subscribe();
  }

  clearVisitsAndUpdateGroup$(): Observable<void> {
    const cameraGroupId = this.existingGroup.id;

    this.dialogErrorMsg = "";
    this.waitingResponse = true;

    if (this.confirmDialogOpened) {
      return this.scheduleService.deleteCameraGroupVisits(cameraGroupId).pipe(
        switchMap(removedVisits => this.updateCameraGroup$(removedVisits)),
        tap(() => {
          this.waitingResponse = false;
          this.clearNightowlVisitsCache();
          
          // If Nightowl group and pause was ON but is now OFF, resume devices
          this.checkAndResumeNightowlOnSave();
          
          this.cameraGroupChanged.emit("CAMERA_GROUP_UPDATED");
          this.modalService.dismissAll();
          this.showForm.next(false);
        }),
        catchError((error) => {
          console.log(error);
          this.dialogErrorMsg = "ERROR";
          return EMPTY;
        })
      );
    }

    console.error("Delete dialog must be opened before clearing visits and updating a camera group");
    return EMPTY;
  }

  updateCameraGroup$(removedVisits: IRemovedScheduledVisit[]): Observable<any> {
    const updatedVisitConfiguration: IVisitConfiguration = this.visitConfigForm;

    this.showSubmitErrorMsg = false;

    const alertDevices: Record<string, string> = this.alertDeviceSelect
      .filter(obj => {
        const array1 = this.existingVisitConfig?.alertSources ?? [];
        const array2 = updatedVisitConfiguration?.alertSources ?? [];
        return [...array1, ...array2].some(device => device.deviceID === obj.value)
      })
      .reduce((acc, obj) => {
        acc[obj.value] = obj.name ? obj.name.replace(` (${obj.value})`, '') : obj.name;
        return acc;
      }, {} as Record<string, string>);

    // When the old visit config had all alarm types selected (eventsToTrig absent/empty),
    // populate it with the full available list so the backend can diff what was removed.
    let oldVisitConfigData = updatedVisitConfiguration ? this.existingVisitConfig : null;
    if (
      oldVisitConfigData &&
      this.availableAlarmTypes?.length > 0 &&
      (!oldVisitConfigData.eventsToTrig || oldVisitConfigData.eventsToTrig.length === 0) &&
      updatedVisitConfiguration?.eventsToTrig?.length > 0
    ) {
      oldVisitConfigData = {
        ...oldVisitConfigData,
        eventsToTrig: this.availableAlarmTypes.map(item => item.key),
      };
    }

    const oldCameraGroupData = this.pauseExpired
      ? { ...this.existingGroup, isPaused: false, pauseStart: undefined, pauseEnd: undefined }
      : this.existingGroup;

    return this.api.updateCameraGroup({
      cameraGroupToUpdate: this.cameraGroupForm as CameraGroupEditForm,
      visitConfigToUpdate: updatedVisitConfiguration,
      oldCameraGroupData,
      oldVisitConfigData: oldVisitConfigData,
      alertDevices: alertDevices,
      removedVisits: removedVisits,
      deviceType: this.selectedDeviceType
    });
  }

  openDialog(dialogEl: any, options: any): void {
    this.confirmDialogOpened = true;

    this.modalService
      .open(dialogEl, options)
      .result.then(
        () => {
          this.confirmDialogOpened = false;
          this.waitingResponse = false;
        },
        () => {
          this.confirmDialogOpened = false;
          this.waitingResponse = false;
        }
      );
  }

  deleteCameraGroup(): void {
    if (this.mode === 'add') return;

    console.log("Deleting camera group...");
    if (this.confirmDialogOpened) {
      const cameraGroupId = this.existingGroup.id;
      const cameraGroupName = this.existingGroup.displayName;
      const visitConfigId = this.existingVisitConfig ? this.existingVisitConfig.id : null;

      this.dialogErrorMsg = "";
      this.waitingResponse = true;

      this.scheduleService.deleteCameraGroupVisits(cameraGroupId).pipe(
        concatMap(removedVisits => this.api.deleteCameraGroup({
          cameraGroupId: cameraGroupId,
          visitConfigId: visitConfigId,
          cameraGroupName: cameraGroupName,
          removedVisits: removedVisits,
          deviceType: this.existingGroup.deviceType
        })
        )).subscribe(deleted => {
          if (deleted) {
            this.modalService.dismissAll();
            this.clearNightowlVisitsCache();
            this.cameraGroupChanged.emit("CAMERA_GROUP_DELETE_SUCCESS");
            this.showForm.next(false);
          } else {
            this.waitingResponse = false;
            this.dialogErrorMsg = "ERROR";
          }
        }, (error) => {
          console.log(error);
          this.waitingResponse = false;
          this.dialogErrorMsg = "ERROR";
        });
    } else {
      console.error("Delete dialog must be opened before deleting a camera group");
    }
  }

  compareNames(a: any, b: any): number {
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  }

  onDeviceTypeSelectChange(): void {
    if (this.selectedDeviceType === "Camera") {
      this.cameraGroupForm.deviceType = DeviceType.Camera;
      this.visitTimeMax = 120;
      this.graceTimeDisabled = false;
    } else if (this.selectedDeviceType === "Nightowl") {
      this.cameraGroupForm.deviceType = DeviceType.Nightowl;
      this.visitConfigForm.options.visitTimeMinutes = 30;
      this.visitTimeMax = 30;
      this.graceTimeDisabled = true;
    } else {
      this.visitTimeMax = 120;
      this.graceTimeDisabled = false;
    }

    if (this.mode === 'add') {
      this.selectedLocation = null;
      this.cameraGroupForm.locationId = null;
      this.initCameraSelect();
    }
  }

  onLocationSelectChange(): void {
    const location: ISelectedLocation | string = this.selectedLocation;

    if (typeof location === 'string' && location === 'undefined') {
      this.cameraSelectDisabled.next(true);
      this.cameraGroupForm.locationId = null;
      return;
    }

    // Empty the selected teams and cameras when selected location is changed
    this.cameraGroupForm.userGroups = [];
    this.cameraGroupForm.cameras = [];
    this.cameraGroupForm.locationId = location.id;

    // Empty also all the camera alert devices, since all the cameras are removed from the group
    if (this.visitConfigForm) {
      this.visitConfigForm.alertSources = this.visitConfigForm.alertSources.filter(d =>
        !d.deviceID.startsWith("34")
      );
    }
    this.refreshAlarmTypeOptions();
    this.syncAlarmTypesToConfig();

    // Enable the camera control if a location is selected
    this.cameraSelectDisabled.next(false);

    // Select the cameras for this location
    this.camerasOfLocation = [...this.selectCamerasOfThisLocation(location.id, this.camerasOfLocation)];

    // Select the origons for this location
    this.origonsOfLocation = [...this.selectOrigonsOfThisLocation(location.id, this.origonsOfLocation)];

    // Reset selected origon when location changes
    this.selectedOrigon = undefined;
    this.selectedOrigonId = undefined;
    if (this.mode === 'edit') {
      (this.cameraGroupForm as CameraGroupEditForm).origonId = null;
    } else {
      (this.cameraGroupForm as NewCameraGroupForm).origonId = "";
    }
    this.origonGsmNumberMissing = false;
    this.receiverTeamSelect = [...this.disableReceiverTeamLocationSelects(this.receiverTeamSelect)];
  }

  onCameraSelectChange(selectedCameras: VideoServerCamera[]): void {
    const currentCameras = this.cameraGroupForm?.cameras ?? [];
    const removedCamera = currentCameras.filter(
      (camera) => !selectedCameras.some((newCamera) => newCamera.discoveryName === camera.discoveryName)
    )[0];

    this.cameraGroupForm.cameras = selectedCameras;

    if (removedCamera && this.visitConfigForm) {
      this.visitConfigForm.alertSources = this.visitConfigForm.alertSources.filter(
        (d) => d.deviceID !== removedCamera.discoveryName
      );
      this.refreshAlarmTypeOptions();
      this.syncAlarmTypesToConfig();
    }
  }

  onAccessModeChange(): void {
    if (this.mode == 'edit') {
      this.clearPauseSchedule();
      (this.cameraGroupForm as CameraGroupEditForm).isPaused = false;
    }
    if (this.cameraGroupForm.accessMode === AccessMode.VisitOnAlert) {
      if (this.mode === 'edit' && !this.existingVisitConfig) {
        this.visitConfigForm = new VisitOnAlertConfig();
      }
      // Clear all cameras when switching to visit on alert
      this.cameraGroupForm.cameras = [];
      if (this.visitConfigForm) {
        this.visitConfigForm.alertSources = [];
      }
    } else {
      // Reset the visit configuration when the access mode is changed from 'visitOnAlert'
      this.cameraGroupForm.cameras.forEach(camera => {
        camera.motionDetection.alertVisit = false;
      });

      // Empty the alert sources when the access mode is changed from 'visitOnAlert'
      if (this.visitConfigForm) {
        this.visitConfigForm.alertSources = [];
        this.selectedAlertDevices = [];
      }
    }
    this.refreshAlarmTypeOptions();
    this.syncAlarmTypesToConfig();
    this.setNightowlDefaultVisitWithAlert();
  }

  onOrigonSelectedChange(selectedIds: string[]): void {
    // Handle single selection - take the last selected item
    const origonId = selectedIds.length > 0 ? selectedIds[selectedIds.length - 1] : null;

    if (origonId) {
      this.selectedOrigon = this.origonSelect.find(o => o.id === origonId);
      this.selectedOrigonId = origonId;
    } else {
      this.selectedOrigon = undefined;
      this.selectedOrigonId = undefined;
    }

    if (this.selectedOrigon) {
      this.onOrigonCallSelectChange();
    } else {
      this.origonGsmNumberMissing = false;
      if (this.mode === 'edit') {
        (this.cameraGroupForm as CameraGroupEditForm).origonId = undefined;
      } else {
        (this.cameraGroupForm as NewCameraGroupForm).origonId = "";
      }
    }
  }

  onOrigonCallSelectChange(): void {
    if (this.mode === 'edit') {
      (this.cameraGroupForm as CameraGroupEditForm).origonId = this.selectedOrigon.id;
    } else {
      (this.cameraGroupForm as NewCameraGroupForm).origonId = this.selectedOrigon.id;
    }

    // if gsmNumber is empty or null show error
    if (this.selectedOrigon.id && (!this.selectedOrigon.gsmNumber || this.selectedOrigon.gsmNumber.trim() === "")) {
      this.origonGsmNumberMissing = true;
    } else {
      this.origonGsmNumberMissing = false;
    }
  }

  get locationOrigonSelect() {
    return this.origonSelect.filter(o => this.cameraGroupForm.locationId.startsWith("C02") || o.locationId === this.cameraGroupForm.locationId);
  }

  onVisitWithAlertToggle(event: [boolean, VideoServerCamera]): void {
    const [isEnabled, camera] = event;

    if (isEnabled) {
      if (!this.visitConfigForm) {
        this.visitConfigForm = new VisitOnAlertConfig();
      }
      if (!this.visitConfigForm.alertSources) {
        this.visitConfigForm.alertSources = [];
      }
      this.visitConfigForm.alertSources.push({ deviceID: camera.discoveryName });
    } else {
      this.visitConfigForm.alertSources = this.visitConfigForm?.alertSources.filter(
        (d) => d.deviceID !== camera.discoveryName
      );
    }
    this.refreshAlarmTypeOptions();
    this.syncAlarmTypesToConfig();
    this.setNightowlDefaultVisitWithAlert();
  }

  shouldDisableCameras(options: IOption[]): void {
    if (this.mode === 'add') {
      options.forEach(option => option.disabled = this.isCameraAlreadyAssigned(option.value));
    } else {
      options.forEach(option => option.disabled = this.isCameraDisabled(option.value));
    }
  }

  selectCamerasOfThisLocation(id: string, cameras: IGroupedOption[]): IGroupedOption[] {
    for (const location of cameras) {
      id.startsWith("C02") || location.id === id
        ? location.disabled = false
        : location.disabled = true;

      this.shouldDisableCameras(location.options);
    }

    return cameras;
  }

  selectOrigonsOfThisLocation(id: string, origons: IGroupedOption[]): IGroupedOption[] {
    if (!origons || !Array.isArray(origons)) {
      return [];
    }
    
    for (const location of origons) {
      id.startsWith("C02") || location.id === id
        ? location.disabled = false
        : location.disabled = true;
    }

    return origons;
  }

  isAlertDeviceAlreadyAssigned(deviceId: string): boolean {
    if (!this.assignedCameras || this.assignedCameras.length === 0) {
      return false;
    }
    
    if (this.mode === 'add') {
      return this.assignedCameras.some(group =>
        group.alertTriggerDevices && group.alertTriggerDevices.includes(deviceId)
      );
    } else {
      const editFormId = (this.cameraGroupForm as CameraGroupEditForm)?.id;
      if (!editFormId) {
        return false;
      }
      return this.assignedCameras.some(group =>
        group.groupId !== editFormId &&
        group.alertTriggerDevices && group.alertTriggerDevices.includes(deviceId)
      );
    }
  }

  initCameraSelect(): void {
    if (!this.alertDeviceSelect || !this.locationSelect || !this.cameraList) {
      return;
    }

    this.alertDeviceSelect.forEach(alertDevice => {
      alertDevice.hidden = this.isAlertDeviceAlreadyAssigned(alertDevice.value);
    });

    this.camerasOfLocation = [];

    for (const location of this.locationSelect) {
      const option: IGroupedOption = {
        label: location.name,
        id: location.id,
        disabled: true,
        options: this.cameraList
          .filter((camera) => {
            const isMatchingDeviceType =
              this.selectedDeviceType === "Nightowl"
                ? camera.deviceType === "34-4"
                : camera.deviceType === "34-1";

            return camera.locationId === location.id && isMatchingDeviceType;
          })
          .map((camera) => {
            return {
              name: `${camera.name} (${camera.id})`,
              value: camera.id,
              disabled: false,
            }
          })
      }
      this.camerasOfLocation.push(option);
    }
    this.camerasOfLocation = this.camerasOfLocation.filter(group => group.options.length > 0);

    if (this.mode === 'edit' && this.cameraGroupForm?.locationId) {
      this.camerasOfLocation = [...this.selectCamerasOfThisLocation(this.cameraGroupForm.locationId, this.camerasOfLocation)];
    }
  }

  initOrigonSelect(): void {
    if (!this.locationSelect || !this.origonSelect) {
      return;
    }

    this.origonsOfLocation = [];

    for (const location of this.locationSelect) {
      const option: IGroupedOption = {
        label: location.name,
        id: location.id,
        disabled: true,
        options: this.origonSelect
          .filter((origon) => origon.locationId === location.id)
          .map((origon) => {
            return {
              name: origon.name || origon.id,
              value: origon.id,
              disabled: false,
            }
          })
      }
      this.origonsOfLocation.push(option);
    }
    this.origonsOfLocation = this.origonsOfLocation.filter(group => group.options.length > 0);
  }

  disableReceiverTeamLocationSelects(locations: IGroupedOption[]): IGroupedOption[] {
    const customerId = JSON.parse(localStorage.getItem("customerInfo")).customerId;

    // All receiver teams should be selectable, if selected location is a customer (top-level)
    if (this.cameraGroupForm.locationId === customerId) {
      locations.forEach(location => location.disabled = false);
    } else {
      for (const location of locations) {
        if (
          location.id === this.cameraGroupForm.locationId ||
          location.id === customerId
        ) {
          location.disabled = false;
        } else {
          location.disabled = true;
        }
      }
    }

    return locations;
  }

  parseSelectedDevices(deviceIds: string[]): void {
    const devices: { deviceID: string }[] = [];

    // Ensure that the selected camera alert devices will not be removed when alert devices are set
    const currentCameras = this.visitConfigForm?.alertSources.filter(
      (device) => device.deviceID.startsWith("34")
    ) || [];

    for (const deviceId of deviceIds) {
      devices.push({ deviceID: deviceId });
    }

    this.visitConfigForm.alertSources = [...currentCameras, ...devices];
    this.refreshAlarmTypeOptions();
    this.syncAlarmTypesToConfig();
  }

  setSelectedAlertDevices(deviceIds: { deviceID: string }[]): void {
    for (const obj of deviceIds) {
      // Don't add cameras to the selected alert devices
      if (!obj.deviceID.startsWith("34")) {
        this.selectedAlertDevices.push(obj.deviceID);
      }
    }
    this.refreshAlarmTypeOptions();
    this.syncAlarmTypesToConfig();
  }

  parseSelectedAlarmTypes(selectedKeys: string[]): void {
    this.selectedAlarmTypes = [...selectedKeys];
    const selectedSet = new Set(this.selectedAlarmTypes);
    this.availableAlarmTypes.forEach((item) => item.checked = selectedSet.has(item.keyStr));
    this.syncAlarmTypesToConfig();
  }

  hasAnySelectedAlarmType(): boolean {
    if (!this.availableAlarmTypes?.length) {
      return true;
    }
    return this.availableAlarmTypes.some((item) => item.checked);
  }

  isScheduledNightowlWithAlertSources(): boolean {
    return this.selectedDeviceType === 'Nightowl' &&
      this.cameraGroupForm?.accessMode === undefined &&
      (this.visitConfigForm?.alertSources?.length || 0) > 0;
  }

  private getSelectedAlertSourceDeviceTypes(): string[] {
    const sourceIds = this.visitConfigForm?.alertSources?.map((source) => source.deviceID) || [];
    const deviceTypes = sourceIds
      .map((id) => this.deviceTypeByDeviceId[id] || this.cameraList?.find((camera) => camera.id === id)?.deviceType)
      .filter((deviceType): deviceType is string => !!deviceType);

    return Array.from(new Set(deviceTypes));
  }

  private getAlarmTypeKeyString(key: AlarmTypeKey): string {
    return this.alarmTypeService.getAlarmTypeKeyString(key);
  }

  private getDeviceTypeName(deviceType: string): string {
    return this.alarmTypeService.getDeviceTypeName(deviceType);
  }

  private refreshAlarmTypeOptions(): void {
    const selectedDeviceTypes = this.getSelectedAlertSourceDeviceTypes();
    const activations: IActivationTranslation[] = this.alarmTypeService.getActivationsFromStorage();

    const filtered = activations
      .filter((item) => selectedDeviceTypes.includes(item.deviceType))
      .filter((item) => !this.isTechnicalAlarm(item));

    const uniqueByKey = new Map<string, IAlarmTypeOption>();
    const selected = this.visitConfigForm?.eventsToTrig;
    const selectedSet = new Set((selected || []).map((key) => this.getAlarmTypeKeyString(key)));
    const defaultSelectAll = !selected || selected.length === 0;
    const hasExistingAlarmTypes = this.availableAlarmTypes.length > 0;

    // Track device types that existed before this refresh
    const previouslyAvailableDeviceTypes = new Set(this.availableAlarmTypes.map(a => a.key.devicePrefixClass));

    filtered.forEach((item) => {
      const key: AlarmTypeKey = {
        alarmGroup: item.group,
        alarmNode: item.node,
        devicePrefixClass: item.deviceType,
      };
      const keyStr = this.getAlarmTypeKeyString(key);

      // If device type is new, check all by default. If not, preserve previous selection.
      let checked = false;
      if (defaultSelectAll) {
        checked = true;
      } else if (hasExistingAlarmTypes && !previouslyAvailableDeviceTypes.has(item.deviceType)) {
        checked = true;
      } else {
        checked = selectedSet.has(keyStr);
      }

      if (!uniqueByKey.has(keyStr)) {
        uniqueByKey.set(keyStr, {
          key,
          keyStr,
          alarmName: item.name,
          label: item.name,
          checked,
        });
      }
    });

    this.availableAlarmTypes = Array.from(uniqueByKey.values()).sort((a, b) => {
      if (a.key.devicePrefixClass !== b.key.devicePrefixClass) {
        return this.getDeviceTypeName(a.key.devicePrefixClass).localeCompare(this.getDeviceTypeName(b.key.devicePrefixClass));
      }
      return a.alarmName.localeCompare(b.alarmName);
    });

    // Group by device type name
    const grouped: { [deviceType: string]: IAlarmTypeOption[] } = {};
    this.availableAlarmTypes.forEach((item) => {
      const groupName = this.getDeviceTypeName(item.key.devicePrefixClass);
      if (!grouped[groupName]) grouped[groupName] = [];
      grouped[groupName].push(item);
    });

    this.alarmTypeSelect = Object.keys(grouped).map((groupName) => ({
      label: groupName,
      options: grouped[groupName].map((item) => ({
        value: item.keyStr,
        name: item.label,
        disabled: false,
      })),
    }));

    this.selectedAlarmTypes = this.availableAlarmTypes
      .filter((item) => item.checked)
      .map((item) => item.keyStr);
  }

  private syncAlarmTypesToConfig(): void {
    if (!this.visitConfigForm?.options) {
      return;
    }

    const isNightowlGroup = this.cameraGroupForm?.deviceType === DeviceType.Nightowl;
    if (!isNightowlGroup) {
      delete this.visitConfigForm.eventsToTrig;
      delete this.visitConfigForm.doVisitByAlarmType;
      return;
    }

    if (!this.availableAlarmTypes?.length) {
      delete this.visitConfigForm.eventsToTrig;
      this.visitConfigForm.doVisitByAlarmType = false;
      return;
    }

    const selectedSet = new Set(this.selectedAlarmTypes);
    const selectedKeys = this.availableAlarmTypes
      .filter((item) => selectedSet.has(item.keyStr))
      .map((item) => item.key);

    if (selectedKeys.length === this.availableAlarmTypes.length) {
      delete this.visitConfigForm.eventsToTrig;
      this.visitConfigForm.doVisitByAlarmType = false;
      return;
    }

    this.visitConfigForm.eventsToTrig = selectedKeys;
    this.visitConfigForm.doVisitByAlarmType = true;
  }

  private isTechnicalAlarm(activation: IActivationTranslation): boolean {
    return this.alarmTypeService.isTechnicalAlarm(activation);
  }

  private normalizeAlarmTypesForSave(): void {
    this.syncAlarmTypesToConfig();
  }

  setCount(count: number, option: VisitTimeOption): void {
    switch (option) {
      case VisitTimeOption.visitTimeMinutes:
        this.visitConfigForm.options.visitTimeMinutes =
          this.visitConfigForm.options.visitTimeMinutes + count >= 0 ? count : 0;
        break;
      case VisitTimeOption.visitGraceTimeMinutes:
        this.visitConfigForm.options.visitGraceTimeMinutes =
          this.visitConfigForm.options.visitGraceTimeMinutes + count >= 0
            ? count
            : 0;
        break;
      default:
        break;
    }
  }

  private setNightowlDefaultVisitWithAlert(): void {
    if (
      this.selectedDeviceType === 'Nightowl' &&
      !this.cameraGroupForm.accessMode &&
      this.visitConfigForm?.alertSources.length > 0
    ) {
      this.visitConfigForm.options.visitTimeMinutes = 30;
      this.visitConfigForm.options.visitGraceTimeMinutes = 0;
    }
  }

  validateVisitTimeInput(value: string, option: VisitTimeOption): void {
    let numValue = parseInt(value, 10);
    if (option === VisitTimeOption.visitTimeMinutes) {
      if (isNaN(numValue) || numValue < 0) numValue = 0;
      if (numValue > this.visitTimeMax) numValue = this.visitTimeMax;
      this.visitConfigForm.options.visitTimeMinutes = numValue;
    } else if (option === VisitTimeOption.visitGraceTimeMinutes) {
      if (isNaN(numValue) || numValue < 0) numValue = 0;
      this.visitConfigForm.options.visitGraceTimeMinutes = numValue;
    }
  }

  private isCameraAlreadyAssigned(cameraId: string): boolean {
    if (!this.assignedCameras || this.assignedCameras.length === 0) {
      return false;
    }
    return this.assignedCameras.some(group => group.cameraIds.includes(cameraId));
  }

  isCameraDisabled(cameraId: string): boolean {
    if (!this.assignedCameras || this.assignedCameras.length === 0) {
      return false;
    }
    const editFormId = (this.cameraGroupForm as CameraGroupEditForm)?.id;
    if (!editFormId) {
      return false;
    }
    return this.assignedCameras.some(group => group.groupId !== editFormId && group.cameraIds.includes(cameraId));
  }

  disableSubmitButton(): boolean {
    const needsAlarmTypeSelection = (
      this.cameraGroupForm.accessMode === 'visitOnAlert' ||
      this.isScheduledNightowlWithAlertSources()
    ) && this.availableAlarmTypes.length > 0;

    const baseValidation = !this.cameraGroupForm.isValid() ||
      this.origonGsmNumberMissing ||
      this.visitConfigForm?.options?.visitTimeMinutes > this.visitTimeMax ||
      this.hasDifferentNightowlCustomerIdsSelected() ||
      (this.cameraGroupForm.accessMode === 'visitOnAlert' && this.visitConfigForm?.alertSources.length === 0) ||
      (needsAlarmTypeSelection && !this.hasAnySelectedAlarmType());

    if (this.mode === 'add') {
      return baseValidation || (!this.isNightowlServiceCustomerIdSet && this.selectedDeviceType == 'Nightowl');
    }

    return baseValidation || !!this.pauseScheduleError;
  }

  hasDifferentNightowlCustomerIdsSelected(): boolean {
    if (this.selectedDeviceType !== "Nightowl" || !this.cameraGroupForm?.cameras || this.cameraGroupForm.cameras.length === 0) {
      return false;
    }

    const companyExternalSystemIds = new Set<string>();

    for (const camera of this.cameraGroupForm.cameras) {
      const cameraDevice = this.cameraList?.find(x => x.id === camera.discoveryName);
      if (cameraDevice?.companyExternalSystemId) {
        companyExternalSystemIds.add(cameraDevice.companyExternalSystemId);
      } else {
        companyExternalSystemIds.add("undefined");
      }
    }

    return companyExternalSystemIds.size > 1;
  }

  clearPauseSchedule(): void {
    this.pauseStartDate = "";
    this.pauseStartTime = "";
    this.resumeDate = "";
    this.resumeTime = "";
    this.pauseScheduleError = "";
    
    // Clear the model fields
    if (this.mode === 'edit') {
      const editForm = this.cameraGroupForm as CameraGroupEditForm;
      editForm.pauseStart = undefined;
      editForm.pauseEnd = undefined;
      
      // Recalculate visit count if pause is enabled
      if (editForm.isPaused) {
        this.countPausedVisits();
      }
    }
  }

  validatePauseSchedule(): boolean {
    this.pauseScheduleError = "";

    // If isPaused is false or not in edit mode, no validation needed
    if (!this.isEditMode || !(this.cameraGroupForm as CameraGroupEditForm).isPaused) {
      this.clearPauseSchedule(); // just to be sure it's empty
      return true;
    }

    const hasPauseDate = !!this.pauseStartDate;
    const hasPauseTime = !!this.pauseStartTime;
    const hasResumeDate = !!this.resumeDate;
    const hasResumeTime = !!this.resumeTime;

    // Check if pause date/time is partially filled
    if ((hasPauseDate && !hasPauseTime) || (!hasPauseDate && hasPauseTime)) {
      this.pauseScheduleError = "PAUSE_TIME_INCOMPLETE";
      return false;
    }

    // Check if resume date/time is partially filled
    if ((hasResumeDate && !hasResumeTime) || (!hasResumeDate && hasResumeTime)) {
      this.pauseScheduleError = "RESUME_AT_INCOMPLETE";
      return false;
    }

    const hasPauseStart = hasPauseDate && hasPauseTime;
    const hasResumeAt = hasResumeDate && hasResumeTime;

    // If neither is set, it's valid (pause without schedule)
    if (!hasPauseStart && !hasResumeAt) {
      return true;
    }

    // If only one is set, it's invalid
    if (hasPauseStart && !hasResumeAt) {
      this.pauseScheduleError = "RESUME_AT_REQUIRED";
      return false;
    }
    if (!hasPauseStart && hasResumeAt) {
      this.pauseScheduleError = "PAUSE_TIME_REQUIRED";
      return false;
    }

    // Both are set, validate the duration
    const pauseStart = new Date(`${this.pauseStartDate}T${this.pauseStartTime}`);
    const resumeAt = new Date(`${this.resumeDate}T${this.resumeTime}`);

    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    if (pauseStart < fiveMinutesAgo) {
      this.pauseScheduleError = "PAUSE_TIME_IN_PAST";
      return false;
    }

    if (resumeAt <= pauseStart) {
      this.pauseScheduleError = "RESUME_MUST_BE_AFTER_PAUSE";
      return false;
    }

    const durationHours = (resumeAt.getTime() - pauseStart.getTime()) / (1000 * 60 * 60);
    if (durationHours < 1) {
      this.pauseScheduleError = "MIN_DURATION_1_HOUR";
      return false;
    }

    return true;
  }

  onPauseToggleChange(): void {
    if (!this.isEditMode) return;

    const editForm = this.cameraGroupForm as CameraGroupEditForm;
    
    if (editForm.isPaused) {
      // When paused, count the visits that will be paused
      this.countPausedVisits();
    } else {
      // When toggled off, clear the schedule times
      // Resume will happen on save, not immediately
      this.pauseStartDate = "";
      this.pauseStartTime = "";
      this.resumeDate = "";
      this.resumeTime = "";
      this.pauseScheduleError = "";
      editForm.pauseStart = undefined;
      editForm.pauseEnd = undefined;
      this.showPausedVisitsInfo = false;
      this.pausedVisitsCount = 0;
      this.pausedVisitsMessage = "";
    }
  }

  /**
   * Get Nightowl visits for a specific camera group (with caching)
   * @param groupId Camera group ID
   * @returns Observable of filtered visits for the group
   */
  private getNightowlVisitsForGroup(groupId: string): Observable<CameraVisit[]> {
    // If we have cached data, use it
    if (this.cachedNightowlVisits !== null) {
      return new Observable(observer => {
        observer.next(this.cachedNightowlVisits.filter(visit => visit.cameraGroupId === groupId));
        observer.complete();
      });
    }
    
    // Otherwise, fetch from API and cache
    return this.api.getNightowlVisits().pipe(
      tap((allVisits: CameraVisit[]) => {
        this.cachedNightowlVisits = allVisits;
      }),
      map((allVisits: CameraVisit[]) => allVisits.filter(visit => visit.cameraGroupId === groupId))
    );
  }

  /**
   * Clear the cached Nightowl visits
   */
  private clearNightowlVisitsCache(): void {
    this.cachedNightowlVisits = null;
  }

  /**
   * Check if pause was turned off on save and resume Nightowl devices if needed
   */
  private checkAndResumeNightowlOnSave(): void {
    // Only applies to Nightowl groups in edit mode
    if (this.mode !== 'edit' || this.selectedDeviceType !== "Nightowl") {
      return;
    }

    const currentPauseState = (this.cameraGroupForm as CameraGroupEditForm).isPaused || false;

    // If pause was ON originally and is now OFF, resume the devices
    if (this.originalPauseState === true && currentPauseState === false) {
      this.checkForOngoingVisitAndResumeNightowl();
    } 
  }

  /**
   * Check if there's an ongoing visit and resume Nightowl devices if applicable
   */
  private checkForOngoingVisitAndResumeNightowl(): void {
    // Only check if this is a Nightowl group
    if (this.selectedDeviceType !== "Nightowl") {
      return;
    }

    const groupId = this.existingGroup?.id || this.cameraGroupForm?.id;
    if (!groupId) {
      return;
    }

    
    // Fetch Nightowl visits using cached data if available
    this.getNightowlVisitsForGroup(groupId).subscribe({
      next: (visits: CameraVisit[]) => {
        const now = new Date();
        
        // Find a visit that should be happening right now
        const currentVisit = visits.find(visit => {
          if (visit.activeVisit) return false; // Skip already active visits
          
          const visitStart = new Date(visit.startDate);
          const visitEnd = new Date(visitStart.getTime() + visit.duration * 60000);
          
          // Check if current time falls within visit time window (including grace period)
          const gracePeriodMs = (visit.gracePeriod || 0) * 60000;
          const visitEndWithGrace = new Date(visitEnd.getTime() + gracePeriodMs);
          
          const isOngoing = now >= visitStart && now <= visitEndWithGrace;
          
          if (isOngoing) {
            console.log('Found ongoing visit:', {
              visitId: visit.id,
              startDate: visit.startDate,
              duration: visit.duration,
              gracePeriod: visit.gracePeriod
            });
          }
          
          return isOngoing;
        });
        
        if (currentVisit) {
          this.resumeNightowlDevices(currentVisit.id);
        } 
      },
      error: (error) => {
        console.error("Error checking for ongoing visit:", error);
      }
    });
  }

  /**
   * Get Nightowl devices from the current camera group
   * @returns Array of Nightowl devices with their hepro details
   */
  private getNightowlDevices(): { externalSystemId: string, companyExternalSystemId: string }[] {
    if (this.selectedDeviceType !== "Nightowl" || !this.cameraGroupForm?.cameras || !this.cameraList) {
      return [];
    }

    const nightowlDevices: { externalSystemId: string, companyExternalSystemId: string }[] = [];

    for (const camera of this.cameraGroupForm.cameras) {
      const cameraDevice = this.cameraList?.find(x => x.id === camera.discoveryName);
      
      // Get required hepro identifiers
      if (cameraDevice?.externalSystemId && cameraDevice?.companyExternalSystemId) {
        nightowlDevices.push({
          externalSystemId: cameraDevice.externalSystemId,
          companyExternalSystemId: cameraDevice.companyExternalSystemId
        });
      }
    }

    return nightowlDevices;
  }

  /**
   * Send resume (WATCH) command to Nightowl devices via backend API
   */
  private resumeNightowlDevices(visitScheduleId?: string): void {
    const nightowlDevices = this.getNightowlDevices();
    
    if (nightowlDevices.length === 0) {
      return;
    }

    const groupId = this.existingGroup?.id || this.cameraGroupForm?.id;
    
    const requestData = {
      cameraGroupId: groupId,
      devices: nightowlDevices,
      visitScheduleId: visitScheduleId
    };

    this.api.resumeNightowlDevices(requestData).subscribe({
      next: (response) => {
        console.log("Nightowl devices resume response:", response);
      },
      error: (error) => {
        console.error("Error resuming Nightowl devices:", error);
      }
    });
  }

  /**
   * Count visits that fall within the pause period
   */
  private countPausedVisits(): void {
    const groupId = this.existingGroup?.id || this.cameraGroupForm?.id;
    if (!groupId) {
      return;
    }
    
    this.loadingVisitsCount = true;
    this.showPausedVisitsInfo = false;
    
    // Use different API endpoints based on device type
    const visitsObservable = this.selectedDeviceType === "Nightowl"
      ? this.getNightowlVisitsForGroup(groupId)
      : this.scheduleService.getAllCameraGroupVisits(groupId);
    
    visitsObservable.subscribe({
      next: (visits: CameraVisit[]) => {
        const parsedPauseStart = (dateStr: string, timeStr: string): Date | null => {
          if (!dateStr || !timeStr) return null;
          const [year, month, day] = dateStr.split('-').map(Number);
          const [hours, minutes] = timeStr.split(':').map(Number);
          return new Date(year, month - 1, day, hours, minutes);
        };

        const pauseStart = parsedPauseStart(this.pauseStartDate, this.pauseStartTime);
        const pauseEnd = parsedPauseStart(this.resumeDate, this.resumeTime);
        
        let pausedCount = 0;
        let visitsToPause = [];
        
        if (!pauseStart && !pauseEnd) {
          // If no pause times are set, count all visits for the camera group
          visitsToPause = visits;
          pausedCount = visits.length;
        } else if (pauseStart && pauseEnd) {
          // Count visits that fall within the pause period.
          // from startDate combined with time.hour/time.minute for correct local time.
          const parseVisitDateAsLocal = (visit: CameraVisit): Date => {
            const datePart = String(visit.startDate).substring(0, 10); // "YYYY-MM-DD"
            const [year, month, day] = datePart.split('-').map(Number);
            const hour = visit.time?.hour ?? 0;
            const minute = visit.time?.minute ?? 0;
            return new Date(year, month - 1, day, hour, minute);
          };

          const now = new Date();
          visitsToPause = visits.filter(visit => {
            let visitDate = parseVisitDateAsLocal(visit);

            // If the visit's date has passed and is not in the pause range,
            // calculate the next occurrence and check that instead
            if (visitDate < now && visit.time) {
              const inRangeAlready = visitDate >= pauseStart && visitDate <= pauseEnd;
              if (!inRangeAlready) {
                const next = new Date(visitDate);
                next.setDate(next.getDate() + 7);
                visitDate = next;
              }
            }

            const inRange = visitDate >= pauseStart && visitDate <= pauseEnd;
            return inRange;
          });
          pausedCount = visitsToPause.length;
        }
        
        this.pausedVisitsCount = pausedCount;
        this.showPausedVisitsInfo = true;
        this.loadingVisitsCount = false;
        
        if (pausedCount > 0) {
          this.pausedVisitsMessage = pausedCount === 1 
            ? "PAUSED_VISITS_SINGULAR" 
            : "PAUSED_VISITS_PLURAL";
        } else {
          this.pausedVisitsMessage = "NO_VISITS_TO_PAUSE";
        }
      },
      error: (error) => {
        console.error("Error fetching visits for pause count:", error);
        this.loadingVisitsCount = false;
        this.showPausedVisitsInfo = false;
      }
    });
  }

  /**
   * Handle changes to pause date/time inputs to recalculate paused visits count
   */
  onPauseDateTimeChange(): void {
    if (this.isEditMode && (this.cameraGroupForm as CameraGroupEditForm).isPaused) {
      if (this.arePauseTimesComplete()) {
        // Run validation only when all fields are filled
        this.validatePauseSchedule();
        if (!this.pauseScheduleError) {
          this.countPausedVisits();
        } else {
          this.showPausedVisitsInfo = false;
        }
      } else {
        // Clear any previous error while fields are still being filled
        this.pauseScheduleError = "";
        this.showPausedVisitsInfo = false;
      }
    }
  }

  /**
   * Check if pause times are in a complete state (all empty or all filled)
   * @returns true if times are completely empty or completely filled, false if partially filled
   */
  arePauseTimesComplete(): boolean {
    const hasStartDate = !!this.pauseStartDate;
    const hasStartTime = !!this.pauseStartTime;
    const hasEndDate = !!this.resumeDate;
    const hasEndTime = !!this.resumeTime;

    // All empty - valid
    if (!hasStartDate && !hasStartTime && !hasEndDate && !hasEndTime) {
      return true;
    }

    // All filled - valid
    if (hasStartDate && hasStartTime && hasEndDate && hasEndTime) {
      return true;
    }

    // Partially filled - invalid
    return false;
  }

  /**
   * Calculate the next occurrence of a recurring visit
   * @param visit The recurring camera visit
   * @returns Date of the next occurrence (startDate + 7 days)
   */
  private getNextOccurrence(visit: CameraVisit): Date {
    const visitDate = new Date(visit.startDate);
    // Add 7 days for the next weekly occurrence
    visitDate.setDate(visitDate.getDate() + 7);
    return visitDate;
  }


  get isPauseStartInvalid(): boolean {
    if (!this.pauseScheduleError) return false;
    return this.pauseScheduleError === "PAUSE_TIME_INCOMPLETE" ||
           this.pauseScheduleError === "PAUSE_TIME_REQUIRED" ||
           this.pauseScheduleError === "PAUSE_TIME_IN_PAST";
  }

  get isResumeAtInvalid(): boolean {
    if (!this.pauseScheduleError) return false;
    return this.pauseScheduleError === "RESUME_AT_INCOMPLETE" || 
           this.pauseScheduleError === "RESUME_AT_REQUIRED" ||
           this.pauseScheduleError === "RESUME_MUST_BE_AFTER_PAUSE" ||
           this.pauseScheduleError === "MIN_DURATION_1_HOUR";
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isAddMode(): boolean {
    return this.mode === 'add';
  }
}
